import { initHeroAnimations } from './animations/hero-animations.js';
import AnimationManager from './animations/animation-manager.js';
import { init as initIntercom } from './utils/intercom.js';
import './markdown-renderer.js'; // Auto-initializes markdown rendering
import gsap from 'gsap';
import { CountUp } from 'countup.js';

class App {
  constructor() {
    this.animationManager = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onDOMReady());
    } else {
      this.onDOMReady();
    }
  }

  onDOMReady() {
    this.initAnimations();
    this.initExistingFeatures();
    initIntercom();
  }

  initAnimations() {
    this.animationManager = new AnimationManager();
    
    setTimeout(() => {
      initHeroAnimations();
    }, 100);
  }

  initExistingFeatures() {
    this.initMobileMenu();
    this.initSubmenuToggles();
    this.initTabSwitcher();
    this.initPricingTabSwitcher();
    this.initPricingPeriodToggle();
    this.initUniversalToggles();
    this.initTransitionTabs();
    this.initDesktopDropdown();
    this.initNewsBanner();
  }

  initMobileMenu() {
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    const mobileMenu = document.getElementById('mobile-menu');
    
    if (mobileMenuBtn && mobileMenu) {
      mobileMenuBtn.addEventListener('click', function() {
        mobileMenu.classList.toggle('hidden');
        
        const svg = mobileMenuBtn.querySelector('svg');
        const isOpen = !mobileMenu.classList.contains('hidden');
        
        if (isOpen) {
          svg.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>';
        } else {
          svg.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>';
        }
      });
      
      const mobileLinks = mobileMenu.querySelectorAll('a[href^="#"]');
      mobileLinks.forEach(link => {
        link.addEventListener('click', () => {
          mobileMenu.classList.add('hidden');
          const svg = mobileMenuBtn.querySelector('svg');
          svg.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>';
        });
      });
    }
  }

  initSubmenuToggles() {
    // List of all submenu sections
    const submenuSections = ['product', 'company', 'resources', 'support', 'legal'];
    
    submenuSections.forEach(section => {
      const toggle = document.getElementById(`${section}-toggle`);
      const submenu = document.getElementById(`${section}-submenu`);
      const arrow = document.getElementById(`${section}-arrow`);
      
      if (toggle && submenu && arrow) {
        toggle.addEventListener('click', function() {
          submenu.classList.toggle('hidden');
          
          if (submenu.classList.contains('hidden')) {
            arrow.style.transform = 'rotate(0deg)';
          } else {
            arrow.style.transform = 'rotate(180deg)';
          }
        });
      }
    });
  }

  initTabSwitcher() {
    const tabButtons = document.querySelectorAll('.tab-btn');
    const tabContents = document.querySelectorAll('.tab-content');
    const tabImages = document.querySelectorAll('.tab-image');
    
    if (tabButtons.length > 0 && tabContents.length > 0) {
      tabButtons.forEach(button => {
        button.addEventListener('click', function() {
          const targetTab = this.getAttribute('data-tab');
          
          tabButtons.forEach(btn => btn.classList.remove('active'));
          this.classList.add('active');
          
          const targetContent = document.getElementById('tab-' + targetTab + '-content');
          const targetImage = document.getElementById('tab-' + targetTab + '-image');
          
          const activeContent = Array.from(tabContents).find(content => content.classList.contains('active'));
          const activeImage = Array.from(tabImages).find(image => image.classList.contains('active'));
          
          if (activeContent === targetContent) return;
          
          const tl = gsap.timeline();
          
          if (activeContent) {
            tl.to(activeContent, {
              opacity: 0,
              x: 5,
              duration: 0.15,
              ease: "power2.inOut",
              onComplete: () => {
                activeContent.classList.remove('active');
                gsap.set(activeContent, { x: 0 });
                if (targetContent) {
                  targetContent.classList.add('active');
                  gsap.fromTo(targetContent, 
                    { opacity: 0, x: -5 }, 
                    { opacity: 1, x: 0, duration: 0.15, ease: "power2.inOut" }
                  );
                }
              }
            });
          } else if (targetContent) {
            targetContent.classList.add('active');
            gsap.fromTo(targetContent, 
              { opacity: 0, x: -5 }, 
              { opacity: 1, x: 0, duration: 0.15, ease: "power2.inOut" }
            );
          }
          
          if (activeImage) {
            tl.to(activeImage, {
              opacity: 0,
              x: -5,
              duration: 0.15,
              ease: "power2.inOut",
              onComplete: () => {
                activeImage.classList.remove('active');
                gsap.set(activeImage, { x: 0 });
                if (targetImage) {
                  targetImage.classList.add('active');
                  gsap.fromTo(targetImage, 
                    { opacity: 0, x: 5 }, 
                    { opacity: 1, x: 0, duration: 0.15, ease: "power2.inOut" }
                  );
                }
              }
            }, "<");
          } else if (targetImage) {
            targetImage.classList.add('active');
            gsap.fromTo(targetImage, 
              { opacity: 0, x: 5 }, 
              { opacity: 1, x: 0, duration: 0.15, ease: "power2.inOut" }
            );
          }
        });
      });
    }
  }

  initPricingTabSwitcher() {
    const pricingTabButtons = document.querySelectorAll('.pricing-tab-btn');
    const pricingTabContents = document.querySelectorAll('.pricing-tab-content');
    
    if (pricingTabButtons.length > 0 && pricingTabContents.length > 0) {
      pricingTabButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const targetTab = e.currentTarget.getAttribute('data-tab');
          
          // Remove active class from all buttons and contents
          pricingTabButtons.forEach(btn => btn.classList.remove('active'));
          pricingTabContents.forEach(content => content.classList.remove('active'));
          
          // Add active class to clicked button
          e.currentTarget.classList.add('active');
          
          // Show target tab content
          const targetContent = document.getElementById(targetTab + '-content');
          if (targetContent) {
            targetContent.classList.add('active');
            
            // Initialize plan selection for the active tab
            this.initPlanSelection(targetContent);
          }
        });
      });
      
      const activeTab = document.querySelector('.pricing-tab-btn.active');
      if (activeTab) {
        const targetTab = activeTab.getAttribute('data-tab');
        const targetContent = document.getElementById(targetTab + '-content');
        if (targetContent) {
          this.initPlanSelection(targetContent);
        }
      }
    }
  }

  initPricingPeriodToggle() {
    const toggle = document.getElementById('annual-toggle');
    if (!toggle) return;

    // Initialize pricing data from backend if available
    this.initializePricingData();

    // Initialize credits annual visibility based on toggle state
    this.updateCreditsAnnualVisibility(toggle.checked);
    
    // Initialize bonus credits label visibility based on toggle state
    this.updateBonusCreditsLabelVisibility(toggle.checked);

    toggle.addEventListener('change', (e) => {
      const isAnnual = e.target.checked;
      
      // Update credits annual visibility
      this.updateCreditsAnnualVisibility(isAnnual);
      
      // Update bonus credits label visibility
      this.updateBonusCreditsLabelVisibility(isAnnual);
      
      // Update pricing data based on toggle state
      this.updatePricingPeriod(isAnnual);
      
      // Trigger custom event for external form handling
      const event = new CustomEvent('pricingPeriodChanged', {
        detail: { 
          isAnnual,
          currentPeriod: isAnnual ? 'annual' : 'monthly'
        }
      });
      document.dispatchEvent(event);
    });
  }

  initUniversalToggles() {
    // Initialize all toggle switches with universal toggle-input class
    const toggles = document.querySelectorAll('.toggle-input');
    
    toggles.forEach(toggle => {
      // Skip pricing-specific toggle (handled separately)
      if (toggle.classList.contains('pricing-period-toggle')) return;
      
      toggle.addEventListener('change', (e) => {
        const toggleId = e.target.id;
        const isChecked = e.target.checked;
        
        // Trigger custom event for external handling
        const event = new CustomEvent('toggleChanged', {
          detail: { 
            toggleId,
            isChecked,
            element: e.target
          }
        });
        document.dispatchEvent(event);
        
        // Log for debugging (can be removed in production)
        console.log(`Toggle ${toggleId} changed to: ${isChecked}`);
      });
    });
  }

  initializePricingData() {
    // This method can be extended to fetch pricing data from backend
    // For now, it validates existing data-attributes
    const annualTab = document.getElementById('annual-content');
    if (!annualTab) return;

    const planOptions = annualTab.querySelectorAll('.plan-option');
    planOptions.forEach(option => {
      this.validatePricingData(option);
    });
  }

  validatePricingData(option) {
    const monthlyData = option.getAttribute('data-monthly');
    const annualData = option.getAttribute('data-annual');
    const planName = option.getAttribute('data-plan');

    if (!monthlyData || !annualData) {
      console.warn(`Missing pricing data for plan: ${planName}`);
      return false;
    }

    try {
      JSON.parse(monthlyData);
      JSON.parse(annualData);
      return true;
    } catch (error) {
      console.warn(`Invalid pricing data format for plan: ${planName}`, error);
      return false;
    }
  }

  updateCreditsAnnualVisibility(isAnnual) {
    const annualTab = document.getElementById('annual-content');
    if (!annualTab) return;

    if (isAnnual) {
      annualTab.classList.add('show-annual');
    } else {
      annualTab.classList.remove('show-annual');
    }
  }

  updateBonusCreditsLabelVisibility(isAnnual) {
    const bonusCreditsLabel = document.querySelector('.bonus-credits-label');
    if (!bonusCreditsLabel) return;

    if (isAnnual) {
      bonusCreditsLabel.classList.add('active');
    } else {
      bonusCreditsLabel.classList.remove('active');
    }
  }

  updatePricingPeriod(isAnnual) {
    // Get all plan options in the annual tab
    const annualTab = document.getElementById('annual-content');
    if (!annualTab) return;

    const planOptions = annualTab.querySelectorAll('.plan-option');
    
    // Update all plan options in the left panel WITH animation
    planOptions.forEach(option => {
      const pricingData = this.getPricingData(option, isAnnual ? 'annual' : 'monthly');
      if (pricingData) {
        // Update current data attributes
        this.updateCurrentDataAttributes(option, pricingData, isAnnual ? 'annual' : 'monthly');
        // Update visual elements WITH animation
        this.updateVisualElements(option, pricingData, isAnnual ? 'annual' : 'monthly', true);
      }
    });

    // Update the selected plan details in the right panel WITH animation
    const activeOption = annualTab.querySelector('.plan-option.active');
    if (activeOption) {
      this.updatePlanDetails(activeOption, annualTab, true);
    }
  }

  updatePlanPeriod(option, period, shouldAnimate = false) {
    try {
      // Get pricing data from data-attributes
      const pricingData = this.getPricingData(option, period);
      if (!pricingData) return;

      // Update current data attributes for compatibility
      this.updateCurrentDataAttributes(option, pricingData, period);

      // Update visual elements without animation by default
      this.updateVisualElements(option, pricingData, period, shouldAnimate);

    } catch (error) {
      console.warn('Error updating plan period:', error);
    }
  }

  getPricingData(option, period) {
    const dataAttr = period === 'annual' ? 'data-annual' : 'data-monthly';
    const rawData = option.getAttribute(dataAttr);
    
    if (!rawData) return null;

    try {
      return JSON.parse(rawData);
    } catch (error) {
      console.warn(`Invalid JSON in ${dataAttr}:`, rawData);
      return null;
    }
  }

  updateCurrentDataAttributes(option, data, period) {
    // Update current data attributes that are used by other parts of the code
    option.setAttribute('data-price', data.price);
    option.setAttribute('data-credits', data.credits);
    option.setAttribute('data-price-per-credit', data.pricePerCredit);
    option.setAttribute('data-description', data.description);
    option.setAttribute('data-note', data.note);
  }

  updateVisualElements(option, data, period, shouldAnimate = false) {
    const creditsElement = option.querySelector('div:nth-child(2)');
    const pricePerCreditElement = option.querySelector('div:nth-child(3)');
    const priceElement = option.querySelector('div:nth-child(4)');
    
    // Update credits display
    if (creditsElement) {
      const numberSpan = creditsElement.querySelector('.number');
      if (numberSpan) {
        if (shouldAnimate) {
          this.animateNumber(numberSpan, data.credits, { forceAnimate: true });
        } else {
          numberSpan.textContent = data.credits;
        }
      } else {
        // Fallback for elements without .number span
        creditsElement.textContent = `${data.credits} credits`;
      }
    }
    
    // Update price per credit display
    if (pricePerCreditElement) {
      const numberSpan = pricePerCreditElement.querySelector('.number');
      if (numberSpan) {
        if (shouldAnimate) {
          // Make sure we have correct decimal places for price per credit
          const options = { 
            forceAnimate: true,
            decimalPlaces: 3,
            duration: 0.8
          };
          this.animateNumber(numberSpan, data.pricePerCredit, options);
        } else {
          numberSpan.textContent = data.pricePerCredit;
        }
      } else {
        // Fallback for elements without .number span
        pricePerCreditElement.textContent = `$${data.pricePerCredit} / credit`;
      }
    }
    
    // Update price display
    if (priceElement) {
      const numberSpan = priceElement.querySelector('.number');
      const suffix = period === 'annual' ? ' / year' : ' / mo';
      const isEnterprise = option.getAttribute('data-plan') === 'enterprise';
      const prefix = isEnterprise ? '>' : '';
      
      if (numberSpan) {
        // Update the number
        if (shouldAnimate) {
          this.animateNumber(numberSpan, data.price, { forceAnimate: true });
        } else {
          numberSpan.textContent = data.price;
        }
        
        // Update the suffix after the number span
        const textAfterNumber = priceElement.childNodes;
        let found = false;
        for (let i = 0; i < textAfterNumber.length; i++) {
          if (textAfterNumber[i] === numberSpan) {
            // Replace or add text node after the number span
            if (i + 1 < textAfterNumber.length && textAfterNumber[i + 1].nodeType === Node.TEXT_NODE) {
              textAfterNumber[i + 1].textContent = suffix;
            } else {
              priceElement.insertAdjacentText('beforeend', suffix);
            }
            found = true;
            break;
          }
        }
        
        if (!found) {
          // If we couldn't find the number span in the expected structure, update the whole element
          priceElement.innerHTML = `${prefix}$<span class="number" data-animate="countup">${data.price}</span>${suffix}`;
        }
      } else {
        // Fallback for elements without .number span
        priceElement.textContent = `${prefix}$${data.price}${suffix}`;
      }
    }
  }

  initPlanSelection(tabContent) {
    const planOptions = tabContent.querySelectorAll('.plan-option');
    
    if (planOptions.length > 0) {
      planOptions.forEach(option => option.classList.remove('active'));
      planOptions[0].classList.add('active');
      // Initialize without animation
      this.updatePlanDetails(planOptions[0], tabContent, false);
      // Initialize form inputs
      this.updateFormInputs(planOptions[0]);
    }

    planOptions.forEach(option => {
      option.addEventListener('click', () => {
        planOptions.forEach(opt => opt.classList.remove('active'));
        
        option.classList.add('active');
        
        // Update with animation only for right panel (default behavior)
        this.updatePlanDetails(option, tabContent, true);
      });
    });
  }

  updatePlanDetails(planOption, tabContent, shouldAnimate = true) {
    const planData = {
      plan: planOption.dataset.plan,
      price: planOption.dataset.price,
      credits: planOption.dataset.credits,
      pricePerCredit: planOption.dataset.pricePerCredit,
      description: planOption.dataset.description,
      note: planOption.dataset.note
    };

    // Update form inputs
    this.updateFormInputs(planOption);

    const elementsToUpdate = tabContent.querySelectorAll('[data-update]');
    
    elementsToUpdate.forEach(element => {
      const updateType = element.getAttribute('data-update');
      
      switch(updateType) {
        case 'name':
          if (planData.plan.startsWith('paygo-')) {
            // For paygo name element, look for .number span or animate whole element
            const numberSpan = element.querySelector('.number');
            if (numberSpan && shouldAnimate) {
              this.animateNumber(numberSpan, planData.price, { forceAnimate: true });
            } else if (shouldAnimate) {
              this.animateNumber(element, planData.price);
            } else {
              this.updateElementValue(element, planData.price);
            }
          } else {
            element.textContent = planOption.querySelector('div').textContent;
          }
          break;
          
        case 'price':
          // For price element, look for .number span or animate whole element
          const priceNumberSpan = element.querySelector('.number');
          if (priceNumberSpan) {
            // Update suffix immediately
            const toggle = document.getElementById('annual-toggle');
            const isAnnual = toggle && toggle.checked;
            const suffix = isAnnual ? ' / year' : ' / mo';
            const suffixSpan = element.querySelector('.text-lg');
            if (suffixSpan) {
              suffixSpan.textContent = suffix;
            }
            
            if (shouldAnimate) {
              this.animateNumber(priceNumberSpan, planData.price, { forceAnimate: true });
            } else {
              priceNumberSpan.textContent = planData.price;
            }
          } else if (shouldAnimate) {
            this.animateNumber(element, planData.price);
          } else {
            this.updateElementValue(element, planData.price);
          }
          break;
          
        case 'credits':
          // For credits element, look for .number span or animate whole element
          const creditsNumberSpan = element.querySelector('.number');
          if (creditsNumberSpan && shouldAnimate) {
            this.animateNumber(creditsNumberSpan, planData.credits, { forceAnimate: true });
          } else if (shouldAnimate) {
            this.animateNumber(element, planData.credits);
          } else {
            this.updateElementValue(element, planData.credits);
          }
          break;
          
        case 'price-per-credit':
          // For price-per-credit element, look for .number span or animate whole element
          const ppcNumberSpan = element.querySelector('.number');
          if (ppcNumberSpan) {
            if (shouldAnimate) {
              const options = { 
                forceAnimate: true,
                decimalPlaces: 3,
                duration: 0.8
              };
              this.animateNumber(ppcNumberSpan, planData.pricePerCredit, options);
            } else {
              ppcNumberSpan.textContent = planData.pricePerCredit;
            }
          } else if (shouldAnimate) {
            this.animateNumber(element, planData.pricePerCredit);
          } else {
            this.updateElementValue(element, planData.pricePerCredit);
          }
          break;
          
        case 'description':
          element.textContent = planData.description;
          break;
          
        case 'note':
          element.textContent = planData.note;
          break;
          
        case 'credits-breakdown':
          // Handle credits breakdown for annual plans
          const toggle = document.getElementById('annual-toggle');
          const isAnnual = toggle && toggle.checked;
          
          if (isAnnual && !planData.plan.startsWith('paygo-')) {
            // Find the credits-annual element in the selected plan option
            const creditsAnnualElement = planOption.querySelector('.credits-annual');
            if (creditsAnnualElement) {
              // Extract numbers from the breakdown text (e.g., "300,000 + 50,000")
              const breakdownText = creditsAnnualElement.textContent;
              const numberMatches = breakdownText.match(/[\d,]+/g);
              
              if (numberMatches && numberMatches.length >= 2) {
                // Get the two number spans in the right panel
                const numberSpans = element.querySelectorAll('.number');
                
                if (numberSpans.length >= 2) {
                  // Parse and animate first number (base credits)
                  const firstNumber = this.parseNumericValue(numberMatches[0]);
                  if (shouldAnimate) {
                    this.animateNumber(numberSpans[0], firstNumber, { forceAnimate: true });
                  } else {
                    numberSpans[0].textContent = numberMatches[0];
                  }
                  
                  // Parse and animate second number (bonus credits)
                  const secondNumber = this.parseNumericValue(numberMatches[1]);
                  if (shouldAnimate) {
                    this.animateNumber(numberSpans[1], secondNumber, { forceAnimate: true });
                  } else {
                    numberSpans[1].textContent = numberMatches[1];
                  }
                }
              }
            }
          } else {
            // Hide the breakdown when not in annual mode
            const numberSpans = element.querySelectorAll('.number');
            numberSpans.forEach(span => span.textContent = '0');
          }
          break;
      }
    });
  }

  updateFormInputs(planOption) {
    // Get form input ID and plan value from data attributes
    const formInputId = planOption.getAttribute('data-form-input');
    const planValue = planOption.getAttribute('data-plan-value');
    
    if (formInputId && planValue) {
      const formInput = document.getElementById(formInputId);
      if (formInput) {
        formInput.value = planValue;
      }
    }
  }

  initTransitionTabs() {
    const tabTriggers = document.querySelectorAll('.tab-trigger[data-tab]');
    const tabContents = document.querySelectorAll('.tab-content[data-tab-content]');
    
    if (tabTriggers.length > 0 && tabContents.length > 0) {
      tabTriggers.forEach(trigger => {
        trigger.addEventListener('click', function() {
          const targetTab = this.getAttribute('data-tab');
          
          // Only handle api-docs and faq tabs
          if (targetTab === 'api-docs' || targetTab === 'faq') {
            // Remove active class from all tab triggers
            tabTriggers.forEach(trig => trig.classList.remove('active'));
            
            // Hide all tab contents
            tabContents.forEach(content => {
              content.classList.add('hidden');
            });
            
            // Add active class to clicked trigger
            this.classList.add('active');
            
            // Show target tab content
            const targetContent = document.querySelector(`[data-tab-content="${targetTab}"]`);
            if (targetContent) {
              targetContent.classList.remove('hidden');
            }
          }
        });
      });
    }
  }

  initDesktopDropdown() {
    const dropdownBtns = document.querySelectorAll('[data-dropdown]');
    
    dropdownBtns.forEach(btn => {
      const dropdownId = btn.getAttribute('data-dropdown');
      const dropdownMenu = document.querySelector(`[data-dropdown-menu="${dropdownId}"]`);
      const dropdownContainer = btn.closest('.header-dropdown');
      
      if (dropdownMenu && dropdownContainer) {
        let hoverTimeout;
        
        dropdownContainer.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
          dropdownMenu.classList.add('active');
        });
        
        dropdownContainer.addEventListener('mouseleave', () => {
          hoverTimeout = setTimeout(() => {
            dropdownMenu.classList.remove('active');
          }, 150);
        });
        
        dropdownMenu.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
        });
      }
    });
  }

  initNewsBanner() {
    const banner = document.getElementById('news-banner');
    const closeBtn = document.getElementById('close-banner');
    const COOKIE_NAME = 'enrichlayer-news-banner-hidden';
    let autoHideTimeout = null;
    
    if (!banner || !closeBtn) return;

    // Cookie utility functions
    const setCookie = (name, value, days) => {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    };

    const getCookie = (name) => {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    };

    // Function to hide banner (used for both manual and automatic hiding)
    const hideBanner = () => {
      // Clear auto-hide timeout if it exists
      if (autoHideTimeout) {
        clearTimeout(autoHideTimeout);
        autoHideTimeout = null;
      }
      
      // Hide banner with fade animation
      banner.classList.remove('opacity-100');
      banner.classList.add('opacity-0');
      
      // Set cookie permanently (10 years)
      setCookie(COOKIE_NAME, 'true', 3650); // Hide forever (10 years)
      
      setTimeout(() => {
        banner.style.display = 'none';
      }, 300); // Match the transition duration
    };

    // Check if banner was previously hidden
    const wasHidden = getCookie(COOKIE_NAME);
    if (!wasHidden) {
      // Show banner with fade animation
      banner.style.display = 'block';
      // Force a reflow to ensure the opacity is applied
      banner.offsetHeight;
      // Animate in with fade (opacity: 0 -> opacity: 1)
      banner.classList.remove('opacity-0');
      banner.classList.add('opacity-100');
      
      // Set auto-hide timer for 12 seconds
      autoHideTimeout = setTimeout(() => {
        hideBanner();
      }, 12000);
    }

    // Handle close button
    closeBtn.addEventListener('click', () => {
      hideBanner();
    });
  }

  scrollTo(target, options = {}) {
    if (this.animationManager) {
      this.animationManager.scrollTo(target, options);
    }
  }

  stopScroll() {
    if (this.animationManager) {
      this.animationManager.pauseAnimations();
    }
  }

  startScroll() {
    if (this.animationManager) {
      this.animationManager.resumeAnimations();
    }
  }

  // Universal Toggle Utilities
  static setToggleState(toggleId, isChecked) {
    const toggle = document.getElementById(toggleId);
    if (toggle && toggle.classList.contains('toggle-input')) {
      toggle.checked = isChecked;
      // Trigger change event to maintain consistency
      toggle.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  static getToggleState(toggleId) {
    const toggle = document.getElementById(toggleId);
    return toggle && toggle.classList.contains('toggle-input') ? toggle.checked : null;
  }

  static createToggle(options = {}) {
    const {
      id = 'toggle-' + Date.now(),
      label = 'Toggle switch',
      checked = false,
      className = ''
    } = options;

    const toggleWrapper = document.createElement('label');
    toggleWrapper.className = `toggle-switch ${className}`.trim();

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'toggle-input';
    input.id = id;
    input.setAttribute('aria-label', label);
    if (checked) input.checked = true;

    const track = document.createElement('div');
    track.className = 'toggle-track';

    const slider = document.createElement('div');
    slider.className = 'toggle-slider';

    track.appendChild(slider);
    toggleWrapper.appendChild(input);
    toggleWrapper.appendChild(track);

    return toggleWrapper;
  }


  animateNumber(element, newValue, options = {}) {
    const { forceAnimate = false } = options;
    
    // Check if element should be animated
    if (!forceAnimate && (!element.hasAttribute('data-animate') || element.getAttribute('data-animate') !== 'countup')) {
      // Update immediately without animation
      this.updateElementValue(element, newValue);
      return;
    }

    // Get current value
    const currentText = element.textContent || element.innerText || '0';
    const currentValue = this.parseNumericValue(currentText);
    const targetValue = this.parseNumericValue(newValue);

    // Skip animation if values are the same
    if (Math.abs(currentValue - targetValue) < 0.001) {
      this.updateElementValue(element, newValue);
      return;
    }

    // Default options
    const defaultOptions = {
      startVal: currentValue,
      duration: 0.8,
      useEasing: true,
      useGrouping: true,
      separator: ',',
      decimal: '.',
      decimalPlaces: options.decimalPlaces !== undefined ? options.decimalPlaces : this.getDecimalPlaces(element),
      ...options
    };

    // Remove forceAnimate from defaultOptions as CountUp doesn't need it
    delete defaultOptions.forceAnimate;

    // Create and start CountUp animation
    const countUp = new CountUp(element, targetValue, defaultOptions);

    if (!countUp.error) {
      countUp.start(() => {
        // Ensure final value is properly formatted
        this.updateElementValue(element, newValue);
      });
    } else {
      console.warn('CountUp error:', countUp.error);
      this.updateElementValue(element, newValue);
    }
  }


  parseNumericValue(text) {
    if (!text) return 0;
    
    // Convert to string and trim
    const str = text.toString().trim();
    
    // Try to find a number pattern in the string
    const numberMatch = str.match(/(\d+(?:[,\.]\d+)*(?:\.\d+)?)/);
    if (numberMatch) {
      // Remove commas and parse
      const cleaned = numberMatch[1].replace(/,/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    
    // Fallback: remove all non-numeric characters except dots and minus
    const cleaned = str.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  getDecimalPlaces(element) {
    const updateType = element.getAttribute('data-update');
    
    // If element has class 'number', it's a number span
    if (element.classList.contains('number')) {
      // Check parent for data-update type
      const parent = element.closest('[data-update]');
      if (parent) {
        const parentUpdateType = parent.getAttribute('data-update');
        switch (parentUpdateType) {
          case 'price-per-credit':
            return 4;
          case 'price':
          case 'credits':
          case 'name':
          default:
            return 0;
        }
      }
    }
    
    switch (updateType) {
      case 'price-per-credit':
        return 4;
      case 'price':
      case 'credits':
      case 'name':
      default:
        return 0;
    }
  }

  updateElementValue(element, value) {
    const updateType = element.getAttribute('data-update');
    
    // First try to find a number span within the element
    const numberSpan = element.querySelector('.number');
    
    if (numberSpan) {
      // If there's a number span, just update that
      numberSpan.textContent = value;
    } else {
      // Fallback to old logic for elements without number spans
      switch (updateType) {
        case 'price':
          // Keep existing logic for price formatting with suffix
          const toggle = document.getElementById('annual-toggle');
          const isAnnual = toggle && toggle.checked;
          const suffix = isAnnual ? '/year' : '/mo';
          const planElement = element.closest('[data-plan]');
          const isEnterprise = planElement && planElement.getAttribute('data-plan') === 'enterprise';
          const prefix = isEnterprise ? '>' : '';
          element.innerHTML = `${prefix}$${value}<span class="text-lg">${suffix}</span>`;
          break;
          
        case 'name':
          element.textContent = `$${value}`;
          break;
          
        case 'credits':
          element.textContent = value;
          break;
          
        case 'price-per-credit':
          element.textContent = value;
          break;
          
        default:
          element.textContent = value;
      }
    }
  }
}

const app = new App();
window.app = app;

export default app; 
